import React from 'react'
import { Button, HStack } from '@chakra-ui/react'
import { HiCheck } from 'react-icons/hi'
import { EncoderState, SuggestedChange } from './types'
import { Roles } from '../../../types/Roles'
import { RoleType } from '../../../contexts/Auth'
import { getSubmitterId } from './EncoderStateMachine'

interface EncoderButtonsProps {
    state: EncoderState
    role: RoleType
    userId: string | undefined
    suggestedChange: SuggestedChange | null
    canSubmit: boolean
    canUpdate: boolean
    canApprove: boolean
    canCancel: boolean
    canUndo: boolean
    hasFormikErrors: boolean
    onSubmit: () => void
    onUpdate: () => void
    onApprove: () => void
    onCancel: () => void
    onUndo: () => void
}

export const EncoderButtons: React.FC<EncoderButtonsProps> = ({
    state,
    role,
    userId,
    canSubmit,
    canUpdate,
    canApprove,
    canCancel,
    canUndo,
    hasFormikErrors,
    onSubmit,
    onUpdate,
    onApprove,
    onCancel,
    onUndo,
}) => {
    const isAdmin = role === Roles.Admin
    const submitterId = getSubmitterId(state.submittedBy)
    const isOwner = userId !== undefined && submitterId === userId

    // Determine button label for cancel/reject
    const getCancelLabel = () => {
        if (isAdmin && !isOwner) {
            return 'Reject'
        }
        return 'Cancel submission'
    }

    return (
        <HStack justifyContent="flex-end" spacing="2">
            {/* Update button - owner can update their pending submission */}
            {canUpdate && (
                <Button
                    leftIcon={<HiCheck />}
                    width="fit-content"
                    colorScheme="teal"
                    size="sm"
                    isLoading={state.status === 'UPDATING'}
                    loadingText="Updating"
                    onClick={onUpdate}
                >
                    Update
                </Button>
            )}

            {/* Approve button - admin only */}
            {canApprove && (
                <Button
                    width="fit-content"
                    colorScheme="green"
                    size="sm"
                    isLoading={state.status === 'APPROVING'}
                    loadingText="Approving"
                    isDisabled={state.status === 'UPDATING'}
                    onClick={onApprove}
                >
                    Approve
                </Button>
            )}

            {/* Cancel/Reject button */}
            {canCancel && (
                <Button
                    width="fit-content"
                    colorScheme="orange"
                    size="sm"
                    isLoading={state.status === 'CANCELLING'}
                    loadingText="Cancelling"
                    isDisabled={state.status === 'UPDATING'}
                    onClick={onCancel}
                >
                    {getCancelLabel()}
                </Button>
            )}

            {/* Submit button - when editing with no pending submission */}
            {canSubmit && (
                <Button
                    leftIcon={<HiCheck />}
                    width="fit-content"
                    colorScheme="teal"
                    size="sm"
                    isLoading={state.status === 'SUBMITTING'}
                    loadingText="Submitting"
                    onClick={onSubmit}
                >
                    Submit
                </Button>
            )}

            {/* Undo button - when editing */}
            {canUndo && (
                <Button
                    width="fit-content"
                    variant={!hasFormikErrors ? 'link' : 'solid'}
                    colorScheme="teal"
                    size={!hasFormikErrors ? 'xs' : 'sm'}
                    onClick={onUndo}
                >
                    Undo
                </Button>
            )}
        </HStack>
    )
}
